import {DateTime} from 'luxon';
import React, {FC, useMemo} from 'react';

import {
  build12Or24hOptionsForFormat,
  TimeFormatsEnum,
} from '../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {assertValidDateTime} from '../../../libs/shared/core/src/helpers/datetime/luxonHelpers';
import {useInitialEffect} from '../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {DateTimeFormatterBase} from '../../../libs/shared/ui/src/components/datetime/dateTimeFormatterBase';
import {
  RelativeDateTimeFormatsEnum,
  RelativeDateTimeFormatter,
  RelativeTimeDistance,
  TimeDistanceFormatsEnum,
  useRelativeTimeDistance,
} from '../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {useLegacyInteractionContext} from '../../../libs/shared/ui/src/interactionTracking/interactionContext';
import {DateTimeFormatsEnum} from './dateTimeFormatter';

/*
 * Props.
 */

interface CompactDateTimeFormatterProps {
  /** The date to format. */
  value: number;
  timeFormat: TimeFormatsEnum;
  dateTimeFormat: DateTimeFormatsEnum;
}

/*
 * Component.
 */

const h12Format: Intl.DateTimeFormatOptions = build12Or24hOptionsForFormat(TimeFormatsEnum.H12);
const h24Format: Intl.DateTimeFormatOptions = build12Or24hOptionsForFormat(TimeFormatsEnum.H24);

export const CompactDateTimeFormatter: FC<CompactDateTimeFormatterProps> = (props) => {
  const {value, timeFormat, dateTimeFormat} = props;
  const distance = useRelativeTimeDistance({
    value,
  });
  const dt = useMemo(() => DateTime.fromMillis(value), [value]);
  const timeFormatOptions = useMemo(
    () => buildDateTimeFormatOptions(distance, timeFormat, dt),
    [distance, dt, timeFormat],
  );

  const context = useLegacyInteractionContext();

  useInitialEffect(() => {
    assertValidDateTime(dt, {
      value,
      interactionContext: context,
    });
  });

  if (!dt.isValid) {
    return null;
  }

  if (dateTimeFormat === DateTimeFormatsEnum.RELATIVE) {
    return <RelativeDateTimeFormatter value={props.value} format={RelativeDateTimeFormatsEnum.SHORT} />;
  }

  return <DateTimeFormatterBase value={dt} format={timeFormatOptions} />;
};

/*
 * Helpers.
 */

function buildDateTimeFormatOptions(
  distance: RelativeTimeDistance,
  timeFormat: TimeFormatsEnum,
  date: DateTime,
): Intl.DateTimeFormatOptions {
  const now = DateTime.local();
  const hoursSinceMidnight = now.diff(now.startOf('day'), 'hours').hours;

  const isNow = distance.format === TimeDistanceFormatsEnum.NOW;
  const isInLastHour = distance.format === TimeDistanceFormatsEnum.MINUTE;
  const isEarlierToday =
    distance.format === TimeDistanceFormatsEnum.HOUR && distance.value <= hoursSinceMidnight;

  if (isNow || isInLastHour || isEarlierToday) {
    const baseFormat = timeFormat === TimeFormatsEnum.H12 ? h12Format : h24Format;
    return {
      ...baseFormat,
      timeStyle: 'short',
    };
  }

  if (distance.format === TimeDistanceFormatsEnum.YEAR) {
    return {
      dateStyle: 'short',
    };
  }

  return {
    day: 'numeric',
    month: 'short',
    timeStyle: undefined,
    dateStyle: undefined,
  };
}
